import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Alert, Button, Space, Tooltip, message, type ButtonProps } from 'antd';
import axios from 'axios';
import { DownloadOutlined, PrinterOutlined, ReloadOutlined } from '@ant-design/icons';
import { useT } from '../../../locales';
import { downloadBlob } from '../../../lib/download';
import { descargarReciboPago } from '../api/pagos-service';

const RECEIPT_ACTION = {
  DOWNLOAD: 'download',
  PRINT: 'print',
} as const;

type ReceiptAction = (typeof RECEIPT_ACTION)[keyof typeof RECEIPT_ACTION];
type Translate = ReturnType<typeof useT>;

interface ReceiptPrintContext {
  popup: Window | null;
  pdfViewerReady: boolean;
  releaseUrl: (() => void) | null;
}

type ReceiptActionContextFactory = () => ReceiptPrintContext;
type ReceiptBlobConsumer = (blob: Blob, context: ReceiptPrintContext) => void | Promise<void>;

/**
 * Minimal receipt target. The hook only needs the payment id and its persisted
 * receipt format version; ADELANTADO create responses (PrepagoResult) expose
 * `idPago` and no formato_recibo yet, so callers can build this shape without
 * fabricating a full PagoClienteResponse.
 */
export interface ReceiptTarget {
  id: number;
  formatoRecibo?: number | null;
  /** Select an immutable archived version rather than the latest document. */
  version?: number;
}

export interface UseReceiptActionsResult {
  label: string;
  icon: ReactNode;
  isDownloading: boolean;
  isPrinting: boolean;
  download: () => Promise<void>;
  print: () => Promise<void>;
  viewerUrl: string | null;
  closeViewer: () => void;
}

export interface ReceiptActionButtonsProps {
  actions: Pick<UseReceiptActionsResult, 'download' | 'print' | 'isDownloading' | 'isPrinting'>;
  downloadLabel: string;
  printLabel: string;
  downloadType?: ButtonProps['type'];
  printType?: ButtonProps['type'];
}

export interface ReceiptPdfViewerProps {
  url: string;
  title: string;
  fallbackMessage: string;
  closeLabel: string;
  onClose: () => void;
}

function showReceiptError(error: unknown, t: Translate): void {
  const sessionExpired = axios.isAxiosError(error) && error.response?.status === 401;
  message.error(
    sessionExpired ? t('pages.pagos.receiptSessionExpired') : t('pages.pagos.receiptError'),
  );
}

function openPrintPopup(): Window | null {
  try {
    const popup = window.open('', '_blank');
    return popup && !popup.closed ? popup : null;
  } catch {
    return null;
  }
}

function trackPopupUrl(popup: Window, url: string): () => void {
  let released = false;
  const release = (): void => {
    if (released) return;
    released = true;
    URL.revokeObjectURL(url);
  };

  try {
    popup.addEventListener('pagehide', release, { once: true });
    popup.addEventListener('beforeunload', release, { once: true });
  } catch {
    // The PDF viewer remains usable; cleanup still runs when the parent unmounts.
  }
  return release;
}

function renderPdfPopup(popup: Window, url: string, title: string): void {
  const popupDocument = popup.document;
  if (!popupDocument.body) {
    throw new Error('The PDF viewer window has no document body');
  }

  popupDocument.title = title;
  popupDocument.body.replaceChildren();
  popupDocument.documentElement.style.height = '100%';
  popupDocument.documentElement.style.overflow = 'hidden';
  popupDocument.body.style.margin = '0';
  popupDocument.body.style.height = '100%';
  popupDocument.body.style.overflow = 'hidden';

  const iframe = popupDocument.createElement('iframe');
  iframe.src = url;
  iframe.title = title;
  iframe.style.width = '100%';
  iframe.style.height = '100vh';
  iframe.style.border = '0';
  iframe.style.display = 'block';

  popupDocument.body.append(iframe);
}

export function ReceiptActionButtons({
  actions,
  downloadLabel,
  printLabel,
  downloadType,
  printType,
}: ReceiptActionButtonsProps) {
  return (
    <Space wrap size="middle">
      <Tooltip title={downloadLabel}>
        <Button
          type={downloadType ?? 'primary'}
          icon={<DownloadOutlined aria-hidden />}
          loading={actions.isDownloading}
          disabled={actions.isPrinting}
          aria-label={downloadLabel}
          onClick={() => void actions.download()}
        >
          {downloadLabel}
        </Button>
      </Tooltip>
      <Tooltip title={printLabel}>
        <Button
          type={printType ?? 'default'}
          icon={<PrinterOutlined aria-hidden />}
          loading={actions.isPrinting}
          disabled={actions.isDownloading}
          aria-label={printLabel}
          onClick={() => void actions.print()}
        >
          {printLabel}
        </Button>
      </Tooltip>
    </Space>
  );
}

export function ReceiptPdfViewer({
  url,
  title,
  fallbackMessage,
  closeLabel,
  onClose,
}: ReceiptPdfViewerProps) {
  return (
    <div role="region" aria-label={title}>
      <Alert type="info" showIcon message={fallbackMessage} />
      <iframe
        src={url}
        title={title}
        style={{ width: '100%', height: 'min(60vh, 520px)', border: 0, marginTop: '1rem' }}
      />
      <Button type="link" onClick={onClose}>
        {closeLabel}
      </Button>
    </div>
  );
}

export function useReceiptActions(pago: ReceiptTarget | null): UseReceiptActionsResult {
  const t = useT();
  const [loadingAction, setLoadingAction] = useState<ReceiptAction | null>(null);
  const [viewerUrl, setViewerUrl] = useState<string | null>(null);
  const viewerUrlRef = useRef<string | null>(null);
  const activeContextRef = useRef<ReceiptPrintContext | null>(null);
  const mountedRef = useRef(true);
  const inFlightRef = useRef(false);

  const isReprint = pago?.formatoRecibo != null;
  const label = isReprint ? t('pages.pagos.reprintReceipt') : t('pages.pagos.downloadReceipt');

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      const activeContext = activeContextRef.current;
      if (activeContext?.popup && !activeContext.pdfViewerReady && !activeContext.popup.closed) {
        activeContext.popup.close();
      }
      if (activeContext?.pdfViewerReady && activeContext.popup?.closed) {
        activeContext.releaseUrl?.();
      }

      const url = viewerUrlRef.current;
      if (url) {
        URL.revokeObjectURL(url);
        viewerUrlRef.current = null;
      }
    };
  }, []);

  const replaceViewerUrl = (url: string): void => {
    if (!mountedRef.current) {
      URL.revokeObjectURL(url);
      return;
    }

    const previousUrl = viewerUrlRef.current;
    if (previousUrl) URL.revokeObjectURL(previousUrl);
    viewerUrlRef.current = url;
    setViewerUrl(url);
  };

  const showInlineViewer = (blob: Blob): void => {
    replaceViewerUrl(URL.createObjectURL(blob));
  };

  const closeViewer = (): void => {
    const url = viewerUrlRef.current;
    if (!url) return;
    URL.revokeObjectURL(url);
    viewerUrlRef.current = null;
    setViewerUrl(null);
  };

  const executeReceiptAction = async (
    action: ReceiptAction,
    createContext: ReceiptActionContextFactory,
    consumeBlob: ReceiptBlobConsumer,
  ): Promise<void> => {
    if (!pago || inFlightRef.current) return;

    inFlightRef.current = true;
    setLoadingAction(action);
    const context = createContext();
    activeContextRef.current = context;

    try {
      const blob =
        pago.version == null
          ? await descargarReciboPago(pago.id, isReprint)
          : await descargarReciboPago(pago.id, true, pago.version);
      if (!mountedRef.current) return;
      await consumeBlob(blob, context);
    } catch (error) {
      if (context.popup && !context.pdfViewerReady && !context.popup.closed) {
        context.popup.close();
      }
      if (!context.pdfViewerReady) context.releaseUrl?.();
      if (mountedRef.current) showReceiptError(error, t);
    } finally {
      inFlightRef.current = false;
      if (activeContextRef.current === context) activeContextRef.current = null;
      if (mountedRef.current) setLoadingAction(null);
    }
  };

  const download = async (): Promise<void> => {
    if (!pago) return;

    await executeReceiptAction(
      RECEIPT_ACTION.DOWNLOAD,
      () => ({ popup: null, pdfViewerReady: false, releaseUrl: null }),
      (blob) => downloadBlob(blob, `pago-receipt-${pago.id}.pdf`),
    );
  };

  const print = async (): Promise<void> => {
    if (!pago || inFlightRef.current) return;

    const popup = openPrintPopup();
    await executeReceiptAction(
      RECEIPT_ACTION.PRINT,
      () => ({ popup, pdfViewerReady: false, releaseUrl: null }),
      (blob, context) => {
        if (!context.popup || context.popup.closed) {
          showInlineViewer(blob);
          return;
        }

        const url = URL.createObjectURL(blob);

        try {
          renderPdfPopup(context.popup, url, t('pages.pagos.receiptViewerTitle'));
          const releaseUrl = trackPopupUrl(context.popup, url);
          context.releaseUrl = releaseUrl;
          context.pdfViewerReady = true;
        } catch {
          URL.revokeObjectURL(url);
          showInlineViewer(blob);
        }
      },
    );
  };

  return {
    label,
    icon: isReprint ? <ReloadOutlined /> : <DownloadOutlined />,
    isDownloading: loadingAction === RECEIPT_ACTION.DOWNLOAD,
    isPrinting: loadingAction === RECEIPT_ACTION.PRINT,
    download,
    print,
    viewerUrl,
    closeViewer,
  };
}
